import { TEST_SIGNAL_PATHS, type SignalPathCounts, type WatchOutcome } from "@visioni/a3s-monitoring/app";

/**
 * How the post-trigger watch reads on the page. Split from the component for
 * the reason given in `log-rows.ts`.
 */

/** Human names for the delivery legs, matching the log rows. */
const SIGNAL_PATH_LABELS = {
  camera: "Camera",
  va: "VA",
} as const satisfies Record<(typeof TEST_SIGNAL_PATHS)[number], string>;

/**
 * Outstanding legs as `2 Camera, 1 VA`.
 *
 * Iterates the wire contract's path order rather than the object's own keys, so
 * the phrase does not reorder itself depending on which button was pressed
 * first.
 */
export function formatOutstanding(counts: SignalPathCounts): string {
  return TEST_SIGNAL_PATHS.filter((path) => (counts[path] ?? 0) > 0)
    .map((path) => `${String(counts[path])} ${SIGNAL_PATH_LABELS[path]}`)
    .join(", ");
}

/**
 * Time left as `1m 46s`, or `46s` under a minute.
 *
 * Rounded up: zero is the watch ending, so showing it while still watching
 * reads as a stuck timer.
 */
export function formatTimeRemaining(msRemaining: number): string {
  const totalSeconds = Math.max(0, Math.ceil(msRemaining / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${String(minutes)}m ${String(seconds)}s` : `${String(seconds)}s`;
}

/**
 * The placeholder row's heading. Always "signals" plural, whatever the count —
 * the line names the thing being watched for, not one instance of it.
 */
export function watchingLabel(counts: SignalPathCounts): string {
  return `Watching for test signals (${formatOutstanding(counts)})`;
}

/**
 * The verdict, in one line.
 *
 * Each names what happened rather than grading it — "Some test signals didn't
 * arrive" tells an operator where to look, where "Partial" would only tell them
 * how to feel about it.
 */
const OUTCOME_LABELS = {
  passed: "All test signals arrived",
  partial: "Some test signals didn't arrive",
  failed: "No test signals arrived",
} as const satisfies Record<WatchOutcome, string>;

export function watchOutcomeLabel(outcome: WatchOutcome): string {
  return OUTCOME_LABELS[outcome];
}

/**
 * The counts behind the verdict.
 *
 * A partial names both halves, because which leg came back is the whole
 * diagnostic value of that outcome. The other two name only the half that
 * exists — repeating "Missing: none" on a pass is noise.
 */
export function watchReportCounts(outcome: WatchOutcome, arrived: SignalPathCounts, missing: SignalPathCounts): string {
  if (outcome === "passed") return `Arrived: ${formatOutstanding(arrived)}`;
  if (outcome === "failed") return `Missing: ${formatOutstanding(missing)}`;
  return `Arrived: ${formatOutstanding(arrived)} · Missing: ${formatOutstanding(missing)}`;
}
